import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { ISearchWell } from '../../../models/well/searchWell';
import { IWellViewModel } from '../../../models/well/well.viewmodel';
import { OregonWellDataService } from '../../../services/oregon-well-data.service';
import { AuthService } from '@shared/auth/auth.service';
import { IFuzzySearchResult } from 'app/models/common/fuzzySearchResult';

@Component({
  selector: 'app-add-well',
  templateUrl: './add-well.component.html',
  styleUrls: ['./add-well.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AddWellComponent implements OnInit {
  wellLookup = false;
  showLookupButton = false;
  isLinear = true;
  formLookupGroup: FormGroup;
  formWellGroup: FormGroup;
  panelOpenState = false;
  pageSize = 10;
  postErrorMessages = [];
  tribalLandReadOnly = true;
  showProgressBar = false;
  processing = false;

  constructor(public oregonWellDataService: OregonWellDataService,
    private formBuilder: FormBuilder, private router: Router, public authService: AuthService) {
    this.createFormGroups();
    this.clearWellViewModel();
  }

  ngOnInit(): void {
    this.oregonWellDataService.GetAddWell();
  }

  // #region form

  wellForm = new FormGroup({
    // step 1
    address1: new FormControl(''),
    city: new FormControl(''),
    state: new FormControl(''),
    zip: new FormControl(''),
    county: new FormControl(''),

    // step 2
    address2: new FormControl(''),
    tag: new FormControl(''),
    wellLog: new FormControl(''),
    latitude: new FormControl(''),
    longitude: new FormControl(''),
    overrideLatLong: new FormControl(''),
    datum: new FormControl(''),
    coordinateCollectionMethod: new FormControl(''),
    monitoringLocationSourceMapScale: new FormControl(''),
    monitoringLocationDescription: new FormControl(''),
    monitoringLocationComments: new FormControl(''),
    tribalLand: new FormControl(''),
    tribalLandName: new FormControl(''),
    depth: new FormControl(''),
    monitoringLocationId: new FormControl(''),
    monitoringLocationName: new FormControl(''),
    monitoringLocationType: new FormControl(''),
    wellType: new FormControl(''),
    formationType: new FormControl(''),
    aquiferName: new FormControl(''),
    holeDepth: new FormControl(''),
    holeDepthUnit: new FormControl(''),
    alternateId: new FormControl(''),
    alternateContext: new FormControl(''),
    comment: new FormControl(''),
    township: new FormControl(''),
    range: new FormControl(''),
    section: new FormControl(''),
    taxLot: new FormControl(''),
    dateWellEstablished: new FormControl(''),
    decommissioned: new FormControl(),
    dateDecommissioned: new FormControl('')
  });

  displayedColumns: string[] = ['wellId', 'wellAddress1', 'wellCity', 'wellState', 'wellZip', 'wellCounty', 'wellLog', 'wellTag', 'score'];
  dataSource = new MatTableDataSource<IWellViewModel>(this.oregonWellDataService.wellViewModel.wellsList);
  @ViewChild('TablePaginator') paginator: MatPaginator;
  @ViewChild('TableSort') sort: MatSort;

  @ViewChild(MatSort, { static: false }) set content(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  createFormGroups() {
    this.formLookupGroup = this.formBuilder.group({
      address1: '',
      city: '',
      state: 'OR',
      zip: '',
      county: '',
    });

    this.formWellGroup = this.formBuilder.group({
      address2: '',
      tag: '',
      latitude: [{ value: '', disabled: false }],
      longitude: [{ value: '', disabled: false }],
      overrideLatLong: false,
      datum: [{ value: 'NAD83', disabled: true }],
      coordinateCollectionMethod: [{ value: 'LAMBERT CONIC CONFORMAL', disabled: true }],
      monitoringLocationSourceMapScale: '',
      monitoringLocationDescription: '',
      monitoringLocationComments: '',
      tribalLand: false,
      tribalLandName: '',
      depth: '',
      samplePoint: '',
      monitoringLocationId: '',
      monitoringLocationName: '',
      monitoringLocationType: '',
      wellType: [{ value: 'Water Supply', disabled: true }],
      wellLog: '',
      formationType: '',
      aquiferName: '',
      holeDepth: '',
      comment: '',
      township: '',
      range: '',
      section: '',
      taxLot: '',
      dateWellEstablished: ''
    });
  }

  getFormValues() {

    // step 1
    this.oregonWellDataService.wellViewModel.address1 = this.formLookupGroup.value.address1;
    this.oregonWellDataService.wellViewModel.city = this.formLookupGroup.value.city;
    this.oregonWellDataService.wellViewModel.state = this.formLookupGroup.value.state;
    this.oregonWellDataService.wellViewModel.zip = this.formLookupGroup.value.zip;
    this.oregonWellDataService.wellViewModel.county = this.formLookupGroup.value.county;

    // step 2
    this.oregonWellDataService.wellViewModel.address2 = this.formWellGroup.value.address2;
    this.oregonWellDataService.wellViewModel.tag = this.formWellGroup.value.tag;
    this.oregonWellDataService.wellViewModel.wellLog = this.formWellGroup.value.wellLog;
    this.oregonWellDataService.wellViewModel.wellType = this.formWellGroup.getRawValue().wellType;
    this.oregonWellDataService.wellViewModel.overrideLatLong = this.formWellGroup.value.overrideLatLong;
    this.oregonWellDataService.wellViewModel.latitude = this.formWellGroup.value.latitude;
    this.oregonWellDataService.wellViewModel.longitude = this.formWellGroup.value.longitude;
    this.oregonWellDataService.wellViewModel.datum = this.formWellGroup.getRawValue().datum;
    this.oregonWellDataService.wellViewModel.coordinateCollectionMethod = this.formWellGroup.getRawValue().coordinateCollectionMethod;
    this.oregonWellDataService.wellViewModel.monitoringLocationSourceMapScale = this.formWellGroup.value.monitoringLocationSourceMapScale;
    this.oregonWellDataService.wellViewModel.monitoringLocationDescription = this.formWellGroup.value.monitoringLocationDescription;
    this.oregonWellDataService.wellViewModel.monitoringLocationComments = this.formWellGroup.value.monitoringLocationComments;
    this.oregonWellDataService.wellViewModel.tribalLand = this.formWellGroup.value.tribalLand;
    this.oregonWellDataService.wellViewModel.tribalLandName = this.formWellGroup.value.tribalLandName;
    this.oregonWellDataService.wellViewModel.depth = this.formWellGroup.value.depth;
    this.oregonWellDataService.wellViewModel.samplePoint = this.formWellGroup.value.samplePoint;
    this.oregonWellDataService.wellViewModel.monitoringLocationId = this.formWellGroup.value.monitoringLocationId;
    this.oregonWellDataService.wellViewModel.monitoringLocationName = this.formWellGroup.value.monitoringLocationName;
    this.oregonWellDataService.wellViewModel.monitoringLocationType = this.formWellGroup.value.monitoringLocationType;
    this.oregonWellDataService.wellViewModel.wellLog = this.formWellGroup.value.wellLog;
    this.oregonWellDataService.wellViewModel.formationType = this.formWellGroup.value.formationType;
    this.oregonWellDataService.wellViewModel.comment = this.formWellGroup.value.comment;
    this.oregonWellDataService.wellViewModel.township = this.formWellGroup.value.township;
    this.oregonWellDataService.wellViewModel.range = this.formWellGroup.value.range;
    this.oregonWellDataService.wellViewModel.section = this.formWellGroup.value.section;
    this.oregonWellDataService.wellViewModel.taxLot = this.formWellGroup.value.taxLot;
    this.oregonWellDataService.wellViewModel.dateWellEstablished = this.formWellGroup.value.dateWellEstablished;

    this.oregonWellDataService.wellViewModel.lastUpdatedBy = this.authService.username;
    this.oregonWellDataService.wellViewModel.decommissioned = this.wellForm.value.decommissioned != null ? (this.wellForm.value.decommissioned == 'Yes' ? true : false) : false;
    this.oregonWellDataService.wellViewModel.dateDecommissioned = this.formWellGroup.value.dateDecommissioned;
  }

  clearStep(step) {
    if (step == 1) {
      this.wellLookup = false;
      this.formLookupGroup.patchValue({
        address1: '',
        city: '',
        state: 'OR',
        zip: '',
        county: ''
      });
      this.oregonWellDataService.wellViewModel.wellsList = [];
    }

    if (step == 2) {
      this.formWellGroup.patchValue({
        address2: '',
        tag: '',
        wellLog: '',
        monitoringLocationSourceMapScale: '',
        monitoringLocationDescription: '',
        monitoringLocationComments: '',
        tribalLand: '',
        tribalLandName: '',
        depth: '',
        samplePoint: '',
        monitoringLocationId: '',
        monitoringLocationName: '',
        monitoringLocationType: '',
        formationType: '',
        comment: '',
        township: '',
        range: '',
        section: '',
        taxLot: '',
        dateWellEstablished: '',
        decommissioned: '',
        dateDecommissioned: ''
      });
    }
  }

  clearWellViewModel() {
    this.oregonWellDataService.wellViewModel = {
      id: 0,
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      county: '',
      latitude: '0',
      longitude: '0',
      overrideLatLong: false,
      datum: '',
      coordinateCollectionMethod: '',
      monitoringLocationSourceMapScale: '',
      monitoringLocationDescription: '',
      monitoringLocationComments: '',
      tribalLand: false,
      tribalLandName: '',
      tag: '',
      wellLog: '',
      wellType: '',
      depth: '',
      samplePoint: '',
      monitoringLocationId: '',
      monitoringLocationName: '',
      monitoringLocationType: '',
      formationType: '',
      comment: '',
      township: '',
      range: '',
      section: '',
      taxLot: '',
      dateWellEstablished: '',
      createDate: '',
      lastUpdated: '',
      createdBy: '',
      lastUpdatedBy: '',
      wellsList: [],
      households: [],
      housingUnits: [],
      wellHousingUnitKey: 0,
      decommissioned: false,
      dateDecommissioned: ''
    }
  }

  // #endregion

  isLookupValid() {
    if (this.formLookupGroup.value.address1.length != 0
      || this.formLookupGroup.value.city.length != 0
      || this.formLookupGroup.value.zip.length != 0
      || this.formLookupGroup.value.county.length != 0) {

      return true;
    }

    return false;
  }

  lookupAddress() {
    if (this.formLookupGroup.value.address1.length != 0
      && this.formLookupGroup.value.zip.length != 0) {
      this.lookupCountyLatLong();
    }
  }

  lookupButton() {
    if (this.formWellGroup.getRawValue().latitude == ""
      && this.formWellGroup.getRawValue().longitude == ""
      && (this.formLookupGroup.value.address1 != "" || this.formLookupGroup.value.zip != "")) {
      this.showLookupButton = true;
    }
  }

  lookupCountyLatLong() {
    this.showProgressBar = true;

    if (this.formLookupGroup.value.address1.length != 0) {
      this.oregonWellDataService.lookupCountyLatLong(
        this.formLookupGroup.value.address1 + "," + this.formLookupGroup.value.city + "," + this.formLookupGroup.value.zip).subscribe(result => {

          this.formLookupGroup.patchValue({
            county: result[3]
          });

          this.formWellGroup.patchValue({
            latitude: result[5],
            longitude: result[4],
          });
        },
          error => {
            this.oregonWellDataService.onHttpError(error);
          },
          () => {
            this.showProgressBar = false;
          }
        );
    }
  }

  lookupWell() {
    if (this.isLookupValid()) {
      this.searchWell.address = this.formLookupGroup.value.address1;
      this.searchWell.city = this.formLookupGroup.value.city;
      this.searchWell.state = this.formLookupGroup.value.state;
      this.searchWell.zip = this.formLookupGroup.value.zip;
      this.searchWell.county = this.formLookupGroup.value.county;
      this.processing = true;
      this.oregonWellDataService.SearchWell(this.searchWell).subscribe(
        result => {
         // this.oregonWellDataService.wellViewModel.wellsList = result;
          this.oregonWellDataService.wellViewModel.wellsList = result.map((result: IFuzzySearchResult) => {
            return {
              wellId: result.data?.wellId || null,
              voucherType: result.data?.voucherType || null,
              voucherId: result.data?.voucherId || null,
              voucherOhaId: result.data?.voucherOhaId || null,
              sampleId: result.data?.sampleId || null,
              wellAddress1: result.data?.wellAddress1 || '',
              wellCity: result.data?.wellCity || '',
              wellZip: result.data?.wellZip || '',
              wellCounty: result.data?.wellCounty || '',
              wellLog: result.data?.wellLog || '',
              wellTag: result.data?.wellTag || '',
              score: result.score || 0
            } as any;
          });

          this.processing = false;
          this.wellLookup = true;
        },
        error => {
          this.oregonWellDataService.onHttpError(error);
          this.processing = false;
        },
        () => {
          this.dataSource = new MatTableDataSource<IWellViewModel>(this.oregonWellDataService.wellViewModel.wellsList);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
          this.processing = false;
        }
      );
    }
    else {
      return this.oregonWellDataService.showToastError("Please select Lookup Criteria");
    }
  }


  onSubmit() {
    this.getFormValues();
    this.oregonWellDataService.AddWell(this.oregonWellDataService.wellViewModel).subscribe(
      result => {
        this.oregonWellDataService.showToastSucess("Well Id " + result.id + " was Added");
        this.router.navigate(['/well/edit/' + result.id]);
      },
      error => {
        this.postErrorMessages = this.oregonWellDataService.onHttpError(error);
      }
    );
  }

  searchWell: ISearchWell = {
    address: '',
    county: '',
    city: '',
    state: '',
    zip: '',
    searchBy: 'Well',
    searchResultsMaxRows: '50',
    wellId: ''
  }

  updateTribalLand() {
    if (this.tribalLandReadOnly) {
      this.tribalLandReadOnly = false;
    }
    else {
      this.tribalLandReadOnly = true;
      this.formWellGroup.patchValue({
        tribalLandName: ''
      });
    }
  }
}